/* =========================================================================
   Friendly Play Theo pop-ins — the scheduler (Eric, 2026-08-16).
   Brother Theo drops in "every now and then" during study mode: a welcome,
   a "take your time" when the player lingers, gentle encouragement after a
   miss, quiet praise on a small streak, one nudge toward the citations, and
   a warm word at the end. Charming, never chatty: hard cap per session, at
   most one pop per trigger family, and the pops never block input (they ride
   the existing triggerCharPop toast).

   Pure decisions live in decideTheoPop (rng injected for tests); the only
   stateful piece is the idle timer, kept here so friendlyController stays a
   thin caller. Line text lives in characters/theo.ts ('friendly-*' pools).
   ========================================================================= */

/** Most pops in one study set, all families combined (finish is exempt —
    the set is over, it can't contribute to chattiness mid-study). */
export const POP_CAP = 4;
/** How long the player must sit on one question before the "no penalty for
    pondering" pop. Long enough to mean "genuinely thinking", not "reading". */
export const PONDER_IDLE_MS = 18_000;
/** Answered-count at which the one-time citation nudge becomes eligible. */
export const SOURCE_NUDGE_AT = 5;

export type TheoPopKind =
  | 'friendly-welcome'
  | 'friendly-ponder'
  | 'friendly-miss'
  | 'friendly-streak'
  | 'friendly-source'
  | 'friendly-finish';

export type TheoEvent = 'start' | 'correct' | 'miss' | 'idle' | 'finish-imperfect';

export interface TheoState {
  pops: number; // total shown this session (finish excluded from cap math)
  run: number; // consecutive correct answers
  ponderShown: boolean;
  streakShown: boolean;
  sourceShown: boolean;
  missPops: number; // capped separately so a rough set isn't a pile-on
}

export function freshTheoState(): TheoState {
  return { pops: 0, run: 0, ponderShown: false, streakShown: false, sourceShown: false, missPops: 0 };
}

/** Decide whether this event earns a pop. MUTATES state (counters/flags) and
    returns the line-pool kind to show, or null. `answered` is the session's
    answered-count AFTER the event's answer (0 for start/idle). */
export function decideTheoPop(
  ev: TheoEvent,
  state: TheoState,
  answered: number,
  rng: () => number = Math.random,
): TheoPopKind | null {
  const capped = state.pops >= POP_CAP;

  switch (ev) {
    case 'start':
      if (!capped && rng() < 0.5) {
        state.pops++;
        return 'friendly-welcome';
      }
      return null;

    case 'idle':
      // The mockup hero — fires once per session, and reliably (no coin flip):
      // a player who is genuinely stuck should always hear it.
      if (!state.ponderShown && !capped) {
        state.ponderShown = true;
        state.pops++;
        return 'friendly-ponder';
      }
      return null;

    case 'correct': {
      state.run++;
      if (state.run === 3 && !state.streakShown && !capped) {
        state.streakShown = true;
        state.pops++;
        return 'friendly-streak';
      }
      // One-time citation nudge, mid-set, on a correct beat (after a miss the
      // player is already reading the citation — no nudge needed there).
      if (answered >= SOURCE_NUDGE_AT && !state.sourceShown && !capped && rng() < 0.5) {
        state.sourceShown = true;
        state.pops++;
        return 'friendly-source';
      }
      return null;
    }

    case 'miss':
      state.run = 0;
      if (state.missPops < 2 && !capped && rng() < 0.4) {
        state.missPops++;
        state.pops++;
        return 'friendly-miss';
      }
      return null;

    case 'finish-imperfect':
      // Perfect sets already get the confetti + 'correct' pop in showEnd; this
      // is the warm word for everyone else. Exempt from the cap — the set is over.
      return rng() < 0.6 ? 'friendly-finish' : null;
  }
}

/* ------------------------------ idle timer ------------------------------ */

let ponderTimer: ReturnType<typeof setTimeout> | null = null;

/** Arm the once-per-question idle watch. Re-arming replaces the old timer. */
export function armPonderTimer(onIdle: () => void, ms: number = PONDER_IDLE_MS): void {
  clearPonderTimer();
  ponderTimer = setTimeout(onIdle, ms);
}

export function clearPonderTimer(): void {
  if (ponderTimer) {
    clearTimeout(ponderTimer);
    ponderTimer = null;
  }
}
